import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import {
  Form,
  Input,
  Checkbox,
  Button,
  Steps,
  message,
  Spin,
  Alert,
  Select,
  Row,
  Col,
  Radio,
} from "antd";
import { Helmet } from "react-helmet";
import { LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { getListingForApply, submitApplication } from "../../lib/api/applyApi";
import { APP_NAME, usStates } from "../../helpers/constants";
import { APP_PRIVACY_ROUTE, APP_TERMS_ROUTE } from "../../helpers/routeNames";
import Logo from "../../components/general/Logo";
import { trackEvent } from "../../helpers/mixpanel";
import { trackProduct } from "../../lib/analytics/trackProduct";

function ApplyBrandChrome({ children }) {
  return (
    <PageShell>
      <BrandBar>
        <BrandBarInner>
          <Logo size="small" color="brand" showTagline={false} linkTo="/" />
          <SecurePill aria-label="Secure application">
            <LockOutlined aria-hidden />
            <SecurePillText>Secure application</SecurePillText>
          </SecurePill>
        </BrandBarInner>
      </BrandBar>
      {children}
      <PageFoot>
        <PageFootInner>
          <span>
            <SafetyCertificateOutlined aria-hidden /> Information is encrypted in
            transit
          </span>
          <PageFootLinks>
            <PageFootLink href={APP_TERMS_ROUTE} target="_blank" rel="noopener noreferrer">
              Terms of Use
            </PageFootLink>
            <PageFootLinkSep aria-hidden>·</PageFootLinkSep>
            <PageFootLink href={APP_PRIVACY_ROUTE} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </PageFootLink>
          </PageFootLinks>
          <span>Powered by {APP_NAME}</span>
        </PageFootInner>
      </PageFoot>
    </PageShell>
  );
}

/** US phone: keep up to 10 digits and format as (XXX) XXX-XXXX while typing. */
function formatPhoneInput(value) {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  if (!digits) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function phoneDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

/** Requires local-part@domain.tld (rejects missing TLD / incomplete domains). */
const STRICT_EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

function PhoneInput({ value, onChange, ...props }) {
  return (
    <Input
      {...props}
      value={value}
      inputMode="tel"
      autoComplete="tel"
      maxLength={14}
      onChange={(e) => onChange?.(formatPhoneInput(e.target.value))}
    />
  );
}

/** Brand mark when a listing has no photos. */
function ListingPhotoPlaceholder() {
  return (
    <PlaceholderWrap aria-hidden="true">
      <PlaceholderLogo src="/logo.png" alt="" />
    </PlaceholderWrap>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 =
        dataUrl.indexOf(",") >= 0 ? dataUrl.split(",")[1] : dataUrl;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const steps = [
  { title: "Personal", key: "personal" },
  { title: "Income & Docs", key: "income" },
  { title: "References", key: "history" },
];

const PublicApplyPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState({
    photoId: null,
    paystub1: null,
    paystub2: null,
  });
  const hasLandlordLawsuit = Form.useWatch("hasLandlordLawsuit", form);
  const landlordLawsuitDetails = Form.useWatch("landlordLawsuitDetails", form);

  const {
    data: listing,
    isLoading: listingLoading,
    isError: listingError,
  } = useQuery({
    queryKey: ["apply-listing", listingId],
    queryFn: () => getListingForApply(listingId),
    enabled: !!listingId,
  });

  const propertyName = listing?.propertyName || "this property";
  const listingTitle = listing?.listingTitle || null;
  const listingAddress = listing?.address || null;
  const photos = Array.isArray(listing?.photos) ? listing.photos : [];
  const mainPhoto = photos[0] || null;
  const sidePhotos = photos.slice(1, 4);
  const showListingTitle =
    !!listingTitle &&
    listingTitle !== listingAddress &&
    listingTitle !== propertyName;

  const handleFileChange = (key, e) => {
    const file = e?.target?.files?.[0];
    if (file) setFileList((prev) => ({ ...prev, [key]: file }));
  };

  const handleNext = async () => {
    try {
      if (currentStep === 1) {
        if (!fileList.photoId) {
          message.error("Please upload a Photo ID.");
          return;
        }
        if (!fileList.paystub1 && !fileList.paystub2) {
          message.error("Please upload at least one paystub.");
          return;
        }
      }
      const fields =
        steps[currentStep].key === "personal"
          ? [
              "name",
              "email",
              "phone",
              "ssnLast4",
              "currentStreet",
              "currentCity",
              "currentState",
              "currentZip",
            ]
          : steps[currentStep].key === "income"
            ? ["employerName", "jobTitle", "monthlyGrossIncome"]
            : [
                "previousLandlordName",
                "previousLandlordPhone",
                "previousLandlordEmail",
                "hasLandlordLawsuit",
                "landlordLawsuitDetails",
                "consentBackgroundCheck",
                "consentTermsPrivacy",
              ];
      await form.validateFields(fields);
      setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    } catch (_) {
      // validation failed
    }
  };

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    try {
      // Validate required fields from every step (unmounted step fields stay in the form store).
      await form.validateFields([
        "name",
        "email",
        "phone",
        "ssnLast4",
        "currentStreet",
        "currentCity",
        "currentState",
        "currentZip",
        "employerName",
        "jobTitle",
        "monthlyGrossIncome",
        "hasLandlordLawsuit",
        "landlordLawsuitDetails",
        "consentBackgroundCheck",
        "consentTermsPrivacy",
      ]);
      const values = form.getFieldsValue(true);
      setSubmitting(true);

      const documents = {};
      if (fileList.photoId)
        documents.photoId = await fileToBase64(fileList.photoId);
      if (fileList.paystub1)
        documents.paystub1 = await fileToBase64(fileList.paystub1);
      if (fileList.paystub2)
        documents.paystub2 = await fileToBase64(fileList.paystub2);
      if (!documents.photoId && !documents.paystub1 && !documents.paystub2) {
        message.error(
          "Please upload at least one document (Photo ID or paystub)."
        );
        setSubmitting(false);
        return;
      }

      const optionalText = (v) => {
        const s = typeof v === "string" ? v.trim() : "";
        return s || undefined;
      };

      await submitApplication({
        listingId,
        personal: {
          name: values.name,
          email: values.email,
          phone: values.phone,
          ssnLast4: String(values.ssnLast4).padStart(4, "0").slice(-4),
          currentStreet: String(values.currentStreet || "").trim(),
          currentCity: String(values.currentCity || "").trim(),
          currentState: values.currentState,
          currentZip: String(values.currentZip || "").trim(),
        },
        income: {
          employerName: values.employerName,
          jobTitle: values.jobTitle,
          monthlyGrossIncome: Number(values.monthlyGrossIncome),
        },
        history: {
          previousLandlordName: optionalText(values.previousLandlordName),
          previousLandlordPhone: optionalText(values.previousLandlordPhone),
          previousLandlordEmail: optionalText(values.previousLandlordEmail),
          hasLandlordLawsuit: values.hasLandlordLawsuit === true,
          landlordLawsuitDetails:
            values.hasLandlordLawsuit === true
              ? optionalText(values.landlordLawsuitDetails)
              : undefined,
          consentBackgroundCheck: true,
          consentTermsPrivacy: true,
        },
        documents,
      });

      trackEvent("rental_application_submitted", {
        listing_id: listingId,
        document_count: Array.isArray(documents) ? documents.length : 0,
      });

      message.success("Application submitted.");
      navigate("/apply/thank-you", { state: { propertyName } });
    } catch (err) {
      console.error("[APPLY] Submission failed", err);
      trackProduct("rental_application_submit_failed", {
        listing_id: listingId,
        error: err?.message || "unknown",
      });
      if (err?.errorFields) {
        console.error("[APPLY] Form validation errors", err.errorFields);
      }
      const msg =
        err?.message && err.message !== "Validation failed"
          ? err.message
          : err?.errorFields
            ? "Please fix the highlighted fields and try again."
            : "Submission failed";
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const stepContent = useMemo(() => {
    if (currentStep === 0) {
      return (
        <>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Full name"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="First and last name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                validateTrigger={["onChange", "onBlur"]}
                rules={[
                  { required: true, message: "Required" },
                  {
                    validator: (_, value) => {
                      const email = String(value || "").trim();
                      if (!email) return Promise.resolve();
                      if (!STRICT_EMAIL_RE.test(email)) {
                        return Promise.reject(
                          new Error("Enter a valid email address")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                normalize={(v) => (typeof v === "string" ? v.trim() : v)}
              >
                <Input
                  type="email"
                  placeholder="you@example.com"
                  size="large"
                  autoComplete="email"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="phone"
                label="Phone"
                validateTrigger={["onChange", "onBlur"]}
                rules={[
                  { required: true, message: "Required" },
                  {
                    validator: (_, value) => {
                      const digits = phoneDigits(value);
                      if (!digits) return Promise.resolve();
                      if (digits.length !== 10) {
                        return Promise.reject(
                          new Error("Enter a 10-digit phone number")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <PhoneInput placeholder="(555) 123-4567" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="ssnLast4"
                label="SSN (last 4 digits)"
                rules={[
                  { required: true, len: 4, message: "Enter 4 digits" },
                  { pattern: /^\d{4}$/, message: "Digits only" },
                ]}
              >
                <Input placeholder="1234" maxLength={4} size="large" />
              </Form.Item>
            </Col>
          </Row>
          <AddressSectionLabel>Current address</AddressSectionLabel>
          <Form.Item
            name="currentStreet"
            label="Street address"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input
              placeholder="123 Main St, Apt 2"
              size="large"
              autoComplete="street-address"
            />
          </Form.Item>
          <Row gutter={12} align="top">
            <Col flex="1 1 160px">
              <Form.Item
                name="currentCity"
                label="City"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input
                  placeholder="City"
                  size="large"
                  autoComplete="address-level2"
                />
              </Form.Item>
            </Col>
            <Col flex="0 0 100px">
              <Form.Item
                name="currentState"
                label="State"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  className="apply-state-select"
                  placeholder="MO"
                  size="large"
                  showSearch
                  optionFilterProp="search"
                  popupMatchSelectWidth={false}
                  options={usStates.map((s) => ({
                    value: s.value,
                    label: s.value,
                    search: `${s.value} ${s.label}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col flex="0 0 120px">
              <Form.Item
                name="currentZip"
                label="ZIP"
                rules={[
                  { required: true, message: "Required" },
                  {
                    pattern: /^\d{5}$/,
                    message: "Enter a 5-digit ZIP",
                  },
                ]}
                normalize={(v) =>
                  typeof v === "string"
                    ? v.replace(/\D/g, "").slice(0, 5)
                    : v
                }
              >
                <Input
                  placeholder="63117"
                  size="large"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={5}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      );
    }
    if (currentStep === 1) {
      return (
        <>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="employerName"
                label="Employer name"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="Company name" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="jobTitle"
                label="Job title"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="Your title" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="monthlyGrossIncome"
                label="Monthly gross income ($)"
                rules={[
                  { required: true, message: "Required" },
                  {
                    type: "number",
                    min: 1,
                    message: "Must be a positive number",
                  },
                ]}
                getValueFromEvent={(e) => {
                  const raw = e?.target?.value ?? "";
                  const digits = String(raw).replace(/\D/g, "");
                  return digits === "" ? undefined : Number(digits);
                }}
                getValueProps={(value) => ({
                  value:
                    value == null || value === ""
                      ? ""
                      : Number(value).toLocaleString("en-US"),
                })}
              >
                <Input
                  size="large"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  placeholder="e.g. 4,500"
                  onKeyDown={(e) => {
                    if (
                      e.key.length === 1 &&
                      !/[0-9]/.test(e.key) &&
                      !e.ctrlKey &&
                      !e.metaKey &&
                      !e.altKey
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <DocsNote>
            Upload a Photo ID and at least one recent paystub (PDF or image).
          </DocsNote>
          <Form.Item label="Photo ID" required>
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileChange("photoId", e)}
            />
            {fileList.photoId && (
              <FileSelected>Selected: {fileList.photoId.name}</FileSelected>
            )}
          </Form.Item>
          <Form.Item label="Paystub 1" required>
            <Input
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => handleFileChange("paystub1", e)}
            />
            {fileList.paystub1 && (
              <FileSelected>Selected: {fileList.paystub1.name}</FileSelected>
            )}
          </Form.Item>
          <Form.Item label="Paystub 2">
            <Input
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => handleFileChange("paystub2", e)}
            />
            {fileList.paystub2 && (
              <FileSelected>Selected: {fileList.paystub2.name}</FileSelected>
            )}
          </Form.Item>
        </>
      );
    }
    return (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="previousLandlordName"
              label="Previous landlord name (optional)"
            >
              <Input placeholder="Name or company" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="previousLandlordPhone"
              label="Previous landlord phone (optional)"
            >
              <Input placeholder="Phone" size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="previousLandlordEmail"
              label="Previous landlord email (optional)"
            >
              <Input type="email" placeholder="Email" size="large" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="hasLandlordLawsuit"
          className="lawsuit-question-item"
          label="Have you ever been involved in a lawsuit with a landlord (including eviction or unpaid rent cases)?"
          rules={[
            {
              required: true,
              message: "Please answer this question",
            },
          ]}
        >
          <StyledLawsuitRadios>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </StyledLawsuitRadios>
        </Form.Item>
        {hasLandlordLawsuit === true && (
          <Form.Item
            className="lawsuit-details-item"
            label="Please briefly explain"
            required
          >
            <Form.Item
              name="landlordLawsuitDetails"
              noStyle
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Please add a short explanation",
                },
              ]}
            >
              <LawsuitDetailsTextArea
                rows={4}
                maxLength={2000}
                placeholder="When it happened and what the case was about"
              />
            </Form.Item>
            <LawsuitCharCount>
              {String(landlordLawsuitDetails || "").length} / 2000
            </LawsuitCharCount>
          </Form.Item>
        )}
        <Form.Item
          name="consentBackgroundCheck"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        "You must consent to background and credit check"
                      )
                    ),
            },
          ]}
        >
          <ConsentCheck>
            <Checkbox>
              I consent to the landlord running a background and credit check
              for this application.
            </Checkbox>
          </ConsentCheck>
        </Form.Item>
        <Form.Item
          name="consentTermsPrivacy"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        "You must agree to the Terms of Use and Privacy Policy"
                      )
                    ),
            },
          ]}
        >
          <ConsentCheck>
            <Checkbox>
              By submitting this application, I agree to LandlordOps&apos;{" "}
              <ConsentLink
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Use
              </ConsentLink>{" "}
              and{" "}
              <ConsentLink
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </ConsentLink>
              , including how application information is collected, stored, and
              shared with the landlord.
            </Checkbox>
          </ConsentCheck>
        </Form.Item>
      </>
    );
  }, [currentStep, fileList, hasLandlordLawsuit, landlordLawsuitDetails]);

  if (listingLoading || !listingId) {
    return (
      <ApplyBrandChrome>
        <ShellInner>
          <Centered>
            <Spin size="large" />
          </Centered>
        </ShellInner>
      </ApplyBrandChrome>
    );
  }
  if (listingError || !listing) {
    return (
      <ApplyBrandChrome>
        <ShellInner>
          <Alert
            type="error"
            message="Listing not found"
            description="This application link may be invalid or expired."
            showIcon
          />
          <Button
            type="primary"
            onClick={() => navigate("/")}
            style={{ marginTop: 16 }}
          >
            Go home
          </Button>
        </ShellInner>
      </ApplyBrandChrome>
    );
  }

  const detailsLine = [
    listing.rent != null ? `$${Number(listing.rent).toLocaleString()}/mo` : null,
    listing.bedrooms != null ? `${listing.bedrooms} bed` : null,
    listing.bathrooms != null ? `${listing.bathrooms} bath` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <Helmet>
        <title>
          {listingAddress
            ? `Rental application · ${listingAddress}`
            : `Rental application`}{" "}
          | {APP_NAME}
        </title>
      </Helmet>
      <ApplyBrandChrome>
        <ShellInner>
          <ListingHero>
            <HeroGallery>
              {mainPhoto ? (
                <>
                  <MainImage
                    src={mainPhoto}
                    alt={listingAddress || listingTitle || "Listing"}
                    loading="lazy"
                  />
                  {sidePhotos.length > 0 && (
                    <SideImages $count={sidePhotos.length}>
                      {sidePhotos.map((url, i) => (
                        <SideImage
                          key={`${url}-${i}`}
                          src={url}
                          alt={`${listingAddress || "Listing"} photo ${i + 2}`}
                          loading="lazy"
                        />
                      ))}
                    </SideImages>
                  )}
                </>
              ) : (
                <ListingPhotoPlaceholder />
              )}
            </HeroGallery>
            <ListingMeta>
              <PageEyebrow>Rental application</PageEyebrow>
              <ListingTitle>
                {listingAddress || listingTitle || "This property"}
              </ListingTitle>
              {showListingTitle && listingAddress && (
                <ListingHeadline>{listingTitle}</ListingHeadline>
              )}
              {detailsLine && <ListingDetails>{detailsLine}</ListingDetails>}
            </ListingMeta>
          </ListingHero>

          <ApplyCard>
            <FormIntro>
              Complete the steps below to apply. Required fields are marked with
              an asterisk.{" "}
              <FormIntroNext>
                Your information is sent to the property owner after submission.
                They review it and handle any background checks themselves.
              </FormIntroNext>
            </FormIntro>
            <StyledSteps
              current={currentStep}
              responsive
              labelPlacement="vertical"
              items={steps.map((s) => ({ title: s.title }))}
            />

            <Form
              form={form}
              layout="vertical"
              requiredMark
              initialValues={{
                consentBackgroundCheck: false,
                consentTermsPrivacy: false,
              }}
            >
              {stepContent}
            </Form>

            <ActionsRow>
              {currentStep > 0 && (
                <Button size="large" onClick={handleBack}>
                  Back
                </Button>
              )}
              {currentStep < steps.length - 1 ? (
                <Button type="primary" size="large" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  loading={submitting}
                  onClick={handleSubmit}
                >
                  Submit application
                </Button>
              )}
            </ActionsRow>
          </ApplyCard>
        </ShellInner>
      </ApplyBrandChrome>
    </>
  );
};

export default PublicApplyPage;

const PageShell = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(180deg, #f3f6f1 0%, #f8fbef 42%, #eef4ea 100%);
`;

const BrandBar = styled.header`
  background: #ffffff;
  border-bottom: 1px solid rgba(44, 95, 45, 0.18);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.8);
`;

const BrandBarInner = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const SecurePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: rgba(44, 95, 45, 0.08);
  border: 1px solid rgba(44, 95, 45, 0.22);
  color: var(--color-primary-dark, #2c5f2d);
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;

  .anticon {
    font-size: 0.875rem;
  }

  @media (max-width: 375px) {
    gap: 0;
    padding: 0.4rem;
    justify-content: center;
  }
`;

const SecurePillText = styled.span`
  @media (max-width: 375px) {
    display: none;
  }
`;

const ShellInner = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: 1.5rem 1rem 2rem;
  flex: 1 1 auto;
`;

const PageFoot = styled.footer`
  margin-top: auto;
  border-top: 1px solid rgba(44, 95, 45, 0.12);
  background: rgba(255, 255, 255, 0.72);
`;

const PageFootInner = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  justify-content: space-between;
  align-items: center;
  color: #4b5563;
  font-size: 0.8125rem;

  .anticon {
    margin-right: 0.35rem;
    color: var(--color-primary, #2c5f2d);
  }

  @media (max-width: 640px) {
    flex-direction: column;
    justify-content: center;
    text-align: center;
    gap: 0.35rem;
  }
`;

const PageFootLinks = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const PageFootLinkSep = styled.span`
  color: #9ca3af;
`;

const PageFootLink = styled.a`
  color: #4b5563;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--color-primary-dark, #2c5f2d);
  }
`;

const Centered = styled.div`
  padding: 4rem 1rem;
  text-align: center;
`;

const ListingHero = styled.div`
  margin-bottom: 1.25rem;
`;

const HeroGallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const MainImage = styled.img`
  width: 100%;
  height: 260px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid #d1d5db;
  background: #e5e7eb;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);

  @media (max-width: 640px) {
    height: 200px;
  }
`;

const PlaceholderWrap = styled.div`
  width: 100%;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  border: 1px solid #d1d5db;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  color: #94a3b8;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);

  @media (max-width: 640px) {
    height: 200px;
  }
`;

const PlaceholderLogo = styled.img`
  width: 28%;
  max-width: 96px;
  height: auto;
  object-fit: contain;
  opacity: 0.72;
  filter: drop-shadow(0 1px 2px rgba(15, 23, 42, 0.08));
`;

const SideImages = styled.div`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$count === 1 ? "1fr" : p.$count === 2 ? "1fr 1fr" : "1fr 1fr 1fr"};
  gap: 0.5rem;
`;

const SideImage = styled.img`
  width: 100%;
  height: 110px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #e5e7eb;

  @media (max-width: 640px) {
    height: 84px;
  }
`;

const ListingMeta = styled.div`
  padding: 1rem 1.15rem 1.1rem;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-left: 4px solid var(--color-primary, #2c5f2d);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.05);
`;

const PageEyebrow = styled.div`
  margin: 0 0 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-primary-dark, #2c5f2d);
`;

const ListingTitle = styled.h1`
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  letter-spacing: -0.015em;

  @media (max-width: 640px) {
    font-size: 1.25rem;
  }
`;

const ListingHeadline = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
  line-height: 1.4;
`;

const ListingDetails = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-primary-dark, #2c5f2d);
`;

const ApplyCard = styled.div`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.08);
  padding: 1.75rem 1.75rem 1.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary, #2c5f2d) 0%,
      #4a7c59 100%
    );
  }

  .ant-form-item-label > label {
    color: #111827 !important;
    font-weight: 600;
  }

  .ant-form-item {
    margin-bottom: 18px;
  }

  @media (min-width: 768px) {
    .ant-form-item {
      margin-bottom: 14px;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-number,
  .ant-select-selector {
    border-color: #9ca3af !important;
    color: #111827 !important;
  }

  /* Single border for TextArea (showCount wrapper must not also draw a box) */
  .ant-input-textarea,
  .ant-input-textarea-show-count {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    outline: none !important;
  }

  .ant-input-textarea-affix-wrapper {
    border: 1px solid #9ca3af !important;
    box-shadow: none !important;
  }

  .ant-input-textarea-affix-wrapper > textarea.ant-input,
  .ant-input-textarea-affix-wrapper textarea {
    border: none !important;
    box-shadow: none !important;
  }

  .ant-input-textarea textarea.ant-input {
    border: 1px solid #9ca3af !important;
    color: #111827 !important;
    box-shadow: none !important;
  }

  .ant-input-textarea-affix-wrapper textarea.ant-input {
    border: none !important;
  }

  /* Lawsuit question: asterisk tops first line (mobile + desktop) */
  .lawsuit-question-item .ant-form-item-label > label {
    align-items: flex-start !important;
  }

  .lawsuit-question-item .ant-form-item-label > label.ant-form-item-required::before {
    line-height: 1.4 !important;
    margin-top: 0.1em;
  }

  /* Desktop: lawsuit question left, Yes/No right (one row, two columns) */
  @media (min-width: 768px) {
    .lawsuit-question-item .ant-form-item-row {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      align-items: flex-start;
      width: 100%;
      gap: 1rem;
    }

    .lawsuit-question-item .ant-form-item-label {
      flex: 1 1 auto;
      max-width: none !important;
      padding: 0 !important;
      text-align: left !important;
    }

    .lawsuit-question-item .ant-form-item-label > label {
      white-space: normal !important;
      height: auto !important;
      line-height: 1.4 !important;
      padding-top: 0.35rem;
      align-items: flex-start !important;
    }

    .lawsuit-question-item .ant-form-item-label > label.ant-form-item-required::before {
      line-height: 1.4 !important;
      margin-top: 0.05em;
    }

    .lawsuit-question-item .ant-form-item-control {
      flex: 0 0 auto !important;
      width: auto !important;
      max-width: none !important;
      min-width: 7.5rem;
    }
  }

  /* Prevent double border / search chrome inside searchable Select */
  .ant-select-selection-search,
  .ant-select-selection-search-input {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
    background: transparent !important;
  }

  .ant-select-selection-search-input {
    height: 100% !important;
    -webkit-appearance: none !important;
    appearance: none !important;
  }

  .ant-select-selection-search-input::-webkit-search-decoration,
  .ant-select-selection-search-input::-webkit-search-cancel-button,
  .ant-select-selection-search-input::-webkit-search-results-button,
  .ant-select-selection-search-input::-webkit-search-results-decoration {
    display: none !important;
    -webkit-appearance: none !important;
  }

  .apply-state-select.ant-select .ant-select-selector {
    padding-inline: 11px !important;
  }

  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }

  .ant-input::placeholder,
  textarea::placeholder {
    color: #6b7280 !important;
  }

  .ant-checkbox-wrapper {
    color: #111827;
    font-weight: 500;
  }

  .ant-btn-primary {
    background: var(--color-primary, #2c5f2d);
    border-color: var(--color-primary, #2c5f2d);
  }

  .ant-btn-primary:hover,
  .ant-btn-primary:focus {
    background: var(--color-primary-dark, #234a24) !important;
    border-color: var(--color-primary-dark, #234a24) !important;
  }

  @media (max-width: 640px) {
    padding: 1.25rem 1rem 1.15rem;
  }
`;

const StyledLawsuitRadios = styled(Radio.Group)`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  white-space: nowrap;
  padding-top: 0.2rem;

  .ant-radio-wrapper {
    font-size: 0.9375rem;
    color: #111827;
  }

  .ant-radio-inner {
    width: 18px;
    height: 18px;
    border-width: 2px !important;
    border-color: #111827 !important;
    background: #ffffff !important;
  }

  .ant-radio:hover .ant-radio-inner,
  .ant-radio-wrapper:hover .ant-radio-inner {
    border-color: #111827 !important;
  }

  .ant-radio-checked .ant-radio-inner {
    border-color: #111827 !important;
    background: #ffffff !important;
  }

  .ant-radio-checked .ant-radio-inner::after {
    background-color: #111827 !important;
    transform: scale(0.55) !important;
  }

  .ant-radio-checked::after {
    border-color: #111827 !important;
  }
`;

const LawsuitDetailsTextArea = styled(Input.TextArea)`
  &.ant-input,
  &.ant-input-textarea,
  textarea& {
    border: 1px solid #9ca3af !important;
    box-shadow: none !important;
    color: #111827 !important;
    padding: 8px 11px !important;
    line-height: 1.45 !important;
    margin: 0 !important;
  }

  /* When antd wraps the control, style the inner textarea */
  &.ant-input-textarea {
    border: none !important;
    background: transparent !important;
  }

  &.ant-input-textarea textarea.ant-input,
  & textarea.ant-input {
    border: 1px solid #9ca3af !important;
    box-shadow: none !important;
    color: #111827 !important;
    padding: 8px 11px !important;
    line-height: 1.45 !important;
    margin: 0 !important;
  }
`;

const LawsuitCharCount = styled.div`
  margin-top: 6px;
  text-align: right;
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1;
`;

const FormIntro = styled.p`
  margin: 0 0 1.25rem;
  padding: 0.85rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #374151;
  font-size: 0.9375rem;
  line-height: 1.5;

  @media (min-width: 768px) {
    margin-bottom: 1rem;
    padding: 0.7rem 0.9rem;
  }
`;

const FormIntroNext = styled.span`
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;

  @media (min-width: 641px) {
    display: block;
    margin-top: 0.2rem;
  }
`;

const StyledSteps = styled(Steps)`
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    margin-bottom: 1.1rem;
  }

  .ant-steps-item-title {
    color: #374151 !important;
    font-weight: 600 !important;
    font-size: 0.9rem !important;
  }

  .ant-steps-item-process .ant-steps-item-title {
    color: var(--color-primary-dark, #2c5f2d) !important;
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    background: var(--color-primary, #2c5f2d) !important;
    border-color: var(--color-primary, #2c5f2d) !important;
  }

  .ant-steps-item-process .ant-steps-item-icon {
    background: var(--color-primary, #2c5f2d) !important;
    border-color: var(--color-primary, #2c5f2d) !important;
  }

  .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon,
  .ant-steps-item-process .ant-steps-item-icon > .ant-steps-icon {
    color: #ffffff !important;
  }

  /*
   * Horizontal + labelPlacement=vertical: connectors are .ant-steps-item-tail
   * between icons. Always show gray (except last step); green after finished.
   */
  &.ant-steps-horizontal {
    .ant-steps-item-tail::after {
      background-color: #d1d5db !important;
    }

    .ant-steps-item-finish .ant-steps-item-tail::after {
      background-color: var(--color-primary, #2c5f2d) !important;
    }

    .ant-steps-item-title::after {
      background-color: #d1d5db !important;
    }

    .ant-steps-item-finish .ant-steps-item-title::after {
      background-color: var(--color-primary, #2c5f2d) !important;
    }

    /* Never draw a connector past the final step */
    .ant-steps-item:last-child .ant-steps-item-tail {
      display: none !important;
    }

    .ant-steps-item:last-child .ant-steps-item-title::after {
      display: none !important;
      content: none !important;
    }
  }

  /* Vertical (mobile via responsive): full labels, comfortable spacing */
  &.ant-steps-vertical {
    .ant-steps-item {
      padding-bottom: 0.65rem;
    }

    .ant-steps-item-container {
      display: flex !important;
      align-items: center !important;
    }

    .ant-steps-item-icon {
      margin-top: 0 !important;
      flex-shrink: 0;
    }

    .ant-steps-item-content {
      min-height: auto !important;
      margin-top: 0 !important;
      display: flex !important;
      align-items: center !important;
    }

    .ant-steps-item-title {
      white-space: normal !important;
      line-height: 1.25 !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .ant-steps-item-tail::after {
      background-color: #d1d5db !important;
    }

    .ant-steps-item-finish .ant-steps-item-tail::after {
      background-color: var(--color-primary, #2c5f2d) !important;
    }

    .ant-steps-item:last-child .ant-steps-item-tail {
      display: none !important;
    }
  }
`;

const DocsNote = styled.p`
  margin: 0 0 1rem;
  padding: 0.75rem 0.9rem;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #1f2937;
  font-size: 0.9375rem;
  line-height: 1.45;
`;

const FileSelected = styled.span`
  display: block;
  margin-top: 0.35rem;
  color: var(--color-primary-dark, #2c5f2d);
  font-weight: 600;
  font-size: 0.875rem;
`;

const AddressSectionLabel = styled.div`
  margin: 1.15rem 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
`;

const ConsentCheck = styled.div`
  padding: 0.65rem 0.85rem;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #374151;

  .ant-checkbox-wrapper,
  .ant-checkbox-wrapper span,
  .ant-checkbox-label {
    font-size: 0.875rem !important;
    line-height: 1.4 !important;
    color: #374151;
  }

  .ant-checkbox .ant-checkbox-inner {
    width: 16px;
    height: 16px;
    border-color: #111827 !important;
    background: #ffffff !important;
  }

  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-wrapper:hover .ant-checkbox-inner {
    border-color: #111827 !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background: var(--color-primary, #2c5f2d) !important;
    border-color: var(--color-primary, #2c5f2d) !important;
  }

  .ant-checkbox-checked:hover .ant-checkbox-inner,
  .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner {
    border-color: var(--color-primary, #2c5f2d) !important;
  }
`;

const ConsentLink = styled.a`
  color: var(--color-primary, #2c5f2d);
  font-size: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--color-primary-dark, #234a24);
  }
`;

const ActionsRow = styled.div`
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;
